import { Download, Eye, EyeOff, Upload } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
	AppEnvClient,
	AppEnvListResult,
	AppEnvManifestEntry,
	AppEnvSaveEntry,
	AppEnvValueType,
} from '../../lib/app-env/types';
import { diffEntries, parseEnvBlob, serializeEnvBlob } from '../../lib/app-env/parse-env';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AppEnvPanelProps {
	/** `installed_apps.id` from core (Decision 9e). */
	installedAppId: string;
	/** Manifest-declared env schema. Empty array → empty-state message. */
	schema: AppEnvManifestEntry[];
	/** SDK client. When Group B publishes, pass the `@khal-os/sdk` instance. */
	client: AppEnvClient;
	/** Whether the current user has admin role — gates export + ad-hoc keys. */
	isAdmin?: boolean;
	/** Optional callback for the host shell — fires after a successful save. */
	onSaved?: () => void;
	/**
	 * Optional grouping function. When provided, fields are grouped under
	 * section headers. Receives (appId, key) and returns the group label
	 * or undefined for the default "Configuration" group.
	 */
	grouping?: (key: string) => string | undefined;
}

interface FieldDraft {
	key: string;
	value: string;
	type: AppEnvValueType;
	dirty: boolean;
	revealed?: boolean;
	error?: string;
}

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const COPPER = '#D49355';
const COPPER_SOFT = 'rgba(212,147,85,0.12)';
const textPrimary = 'var(--khal-text-primary, #e5e5e5)';
const textMuted = 'var(--khal-text-muted, #888)';
const borderDefault = 'var(--khal-border-default, rgba(255,255,255,0.08))';
const surfaceRaised = 'var(--khal-surface-raised, rgba(255,255,255,0.04))';
const controlsBg = 'var(--khal-controls-bg, rgba(255,255,255,0.06))';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function inferType(entry: AppEnvManifestEntry | undefined): AppEnvValueType {
	return entry?.type ?? 'string';
}

function validateField(value: string, type: AppEnvValueType, required: boolean): string | undefined {
	if (required && value.trim() === '') return 'Required';
	if (value === '') return undefined;
	if (type === 'url') {
		try {
			new URL(value);
		} catch {
			return 'Invalid URL';
		}
	}
	if (type === 'number' && Number.isNaN(Number(value))) return 'Must be a number';
	return undefined;
}

function buildDrafts(
	schema: AppEnvManifestEntry[],
	list: AppEnvListResult | null,
): FieldDraft[] {
	const valuesByKey = new Map((list?.values ?? []).map((v) => [v.key, v]));
	const schemaByKey = new Map(schema.map((s) => [s.key, s]));
	const out: FieldDraft[] = [];

	for (const declared of schema) {
		const stored = valuesByKey.get(declared.key);
		out.push({
			key: declared.key,
			value: stored?.masked_value ?? declared.default ?? '',
			type: inferType(declared),
			dirty: false,
		});
	}
	// Ad-hoc rows: keys present server-side but not in the manifest.
	for (const stored of list?.values ?? []) {
		if (!schemaByKey.has(stored.key)) {
			out.push({
				key: stored.key,
				value: stored.masked_value ?? '',
				type: stored.type,
				dirty: false,
			});
		}
	}
	return out;
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export function AppEnvPanel({
	installedAppId,
	schema,
	client,
	isAdmin = false,
	onSaved,
	grouping,
}: AppEnvPanelProps) {
	const [list, setList] = useState<AppEnvListResult | null>(null);
	const [drafts, setDrafts] = useState<FieldDraft[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [importOpen, setImportOpen] = useState(false);

	const refresh = useCallback(async () => {
		setLoading(true);
		try {
			const result = await client.list({ installedAppId });
			setList(result);
			setDrafts(buildDrafts(schema, result));
		} finally {
			setLoading(false);
		}
	}, [client, installedAppId, schema]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const dirtyCount = useMemo(() => drafts.filter((d) => d.dirty).length, [drafts]);

	const updateDraft = useCallback(
		(key: string, patch: Partial<FieldDraft>) => {
			setDrafts((prev) =>
				prev.map((d) => {
					if (d.key !== key) return d;
					const next = { ...d, ...patch, dirty: true };
					const declared = schema.find((s) => s.key === key);
					next.error = validateField(next.value, next.type, declared?.required ?? false);
					return next;
				}),
			);
		},
		[schema],
	);

	const handleReveal = useCallback(
		async (key: string) => {
			const draft = drafts.find((d) => d.key === key);
			if (!draft) return;
			if (draft.revealed) {
				setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, revealed: false } : d)));
				return;
			}
			try {
				const { value } = await client.reveal({ installedAppId, key });
				setDrafts((prev) =>
					prev.map((d) => (d.key === key ? { ...d, value, revealed: true } : d)),
				);
			} catch (err) {
				setSaveError(err instanceof Error ? err.message : 'Reveal failed');
			}
		},
		[client, drafts, installedAppId],
	);

	const handleSave = useCallback(async () => {
		const dirty = drafts.filter((d) => d.dirty);
		if (dirty.length === 0) return;
		const errors = dirty.filter((d) => d.error);
		if (errors.length > 0) {
			setSaveError(`Fix ${errors.length} field error${errors.length === 1 ? '' : 's'} first`);
			return;
		}
		const entries: AppEnvSaveEntry[] = dirty.map((d) => ({
			key: d.key,
			value: d.value,
			type: d.type,
		}));
		setSaving(true);
		setSaveError(null);
		try {
			await client.save({ installedAppId, entries });
			onSaved?.();
			await refresh();
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : 'Save failed');
		} finally {
			setSaving(false);
		}
	}, [client, drafts, installedAppId, onSaved, refresh]);

	const handleClaimAdopt = useCallback(async () => {
		if (!list?.claim_pending) return;
		try {
			await client.claim({ installedAppId, sourceKeys: list.claim_keys });
			await refresh();
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : 'Claim failed');
		}
	}, [client, installedAppId, list, refresh]);

	const handleClaimSkip = useCallback(async () => {
		try {
			await client.claim_skip({ installedAppId });
			setList((prev) => (prev ? { ...prev, claim_pending: false, claim_keys: [] } : prev));
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : 'Skip failed');
		}
	}, [client, installedAppId]);

	const handleImport = useCallback(
		(blob: string) => {
			const parsed = parseEnvBlob(blob);
			if (parsed.errors.length > 0) {
				setSaveError(parsed.errors[0]?.message ?? 'Fix import errors first');
				return;
			}
			if (parsed.entries.length === 0) {
				setSaveError('No entries found');
				return;
			}
			setDrafts((prev) => {
				const next = [...prev];
				for (const { key, value } of parsed.entries) {
					const idx = next.findIndex((d) => d.key === key);
					const declared = schema.find((s) => s.key === key);
					const type = declared?.type ?? 'string';
					if (idx >= 0) {
						next[idx] = { ...next[idx]!, value, dirty: true, type };
					} else if (isAdmin) {
						next.push({ key, value, type, dirty: true });
					}
				}
				return next;
			});
			setImportOpen(false);
		},
		[isAdmin, schema],
	);

	const handleExport = useCallback(async () => {
		if (!client.export) {
			setSaveError('Export not available');
			return;
		}
		try {
			const { entries } = await client.export({ installedAppId });
			const blob = serializeEnvBlob(entries);
			const url = URL.createObjectURL(new Blob([blob], { type: 'text/plain' }));
			const a = document.createElement('a');
			a.href = url;
			a.download = `${installedAppId}.env`;
			a.click();
			URL.revokeObjectURL(url);
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : 'Export failed');
		}
	}, [client, installedAppId]);

	// Group fields when grouping prop is provided — MUST be above early returns
	const groupedFields: Array<{ label: string; drafts: FieldDraft[] }> = useMemo(() => {
		if (!grouping) return [{ label: 'Configuration', drafts }];

		const groups = new Map<string, FieldDraft[]>();
		const ungrouped: FieldDraft[] = [];

		for (const draft of drafts) {
			const group = grouping(draft.key);
			if (group) {
				const existing = groups.get(group);
				if (existing) existing.push(draft);
				else groups.set(group, [draft]);
			} else {
				ungrouped.push(draft);
			}
		}

		const result: Array<{ label: string; drafts: FieldDraft[] }> = [];
		for (const [label, groupDrafts] of groups) {
			result.push({ label, drafts: groupDrafts });
		}
		if (ungrouped.length > 0) {
			result.push({ label: 'Configuration', drafts: ungrouped });
		}
		return result;
	}, [drafts, grouping]);

	if (loading) {
		return (
			<div style={{ fontSize: 13, color: textMuted, padding: '16px 0' }}>Loading environment…</div>
		);
	}

	if (schema.length === 0 && (list?.values?.length ?? 0) === 0 && !list?.claim_pending && !isAdmin) {
		return (
			<div style={{ fontSize: 13, color: textMuted, padding: '16px 0' }}>
				This app has no configurable environment variables.
			</div>
		);
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			{/* Intro card */}
			<div
				className="rounded-xl border p-4"
				style={{
					borderColor: borderDefault,
					background: surfaceRaised,
				}}
			>
				<div style={{ fontSize: 13, color: textPrimary, fontWeight: 500, marginBottom: 4 }}>
					Configure provider connections
				</div>
				<div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>
					Secrets are masked by default and only revealed on explicit request. Saving configuration may restart the app runtime.
				</div>
			</div>

			{list?.claim_pending && (
				<ClaimBanner
					keys={list.claim_keys}
					modifiedAt={list.claim_secret_modified_at}
					onAdopt={handleClaimAdopt}
					onSkip={handleClaimSkip}
				/>
			)}

			{/* Toolbar */}
			<div className="flex items-center justify-between" data-testid="appenv-toolbar">
				<div className="flex items-center gap-2" style={{ fontSize: 11, color: textMuted }}>
					{dirtyCount > 0 && (
						<span
							data-testid="appenv-dirty-badge"
							className="rounded-full px-2 py-0.5 font-medium"
							style={{ background: COPPER_SOFT, color: COPPER }}
						>
							{dirtyCount} unsaved
						</span>
					)}
				</div>
				<div className="flex items-center gap-1.5">
					<ToolbarButton onClick={() => setImportOpen(true)} icon={<Upload size={12} />}>
						Import .env
					</ToolbarButton>
					{isAdmin && client.export && (
						<ToolbarButton onClick={handleExport} icon={<Download size={12} />}>
							Export .env
						</ToolbarButton>
					)}
				</div>
			</div>

			{/* Field list — grouped or flat */}
			{groupedFields.map((group) => (
				<div key={group.label} style={{ marginBottom: 4 }}>
					{groupedFields.length > 1 && (
						<div
							style={{
								fontSize: 12,
								fontWeight: 600,
								color: textPrimary,
								marginBottom: 8,
								paddingTop: 4,
							}}
						>
							{group.label}
						</div>
					)}
					<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
						{group.drafts.map((draft) => {
							const declared = schema.find((s) => s.key === draft.key);
							return (
								<EnvField
									key={draft.key}
									draft={draft}
									declared={declared}
									onChange={(value) => updateDraft(draft.key, { value })}
									onReveal={() => handleReveal(draft.key)}
								/>
							);
						})}
					</div>
				</div>
			))}

			{/* Save footer */}
			<div
				className="flex items-center justify-between pt-3"
				style={{ borderTop: `1px solid ${borderDefault}` }}
			>
				<span
					style={{
						fontSize: 12,
						color: saveError ? 'var(--khal-status-error)' : textMuted,
					}}
				>
					{saveError ?? (dirtyCount > 0 ? 'Saving configuration may restart the app runtime.' : '')}
				</span>
				<button
					type="button"
					data-testid="appenv-save"
					disabled={saving || dirtyCount === 0}
					onClick={handleSave}
					className="rounded-lg px-4 py-2 text-[12px] font-semibold transition-colors"
					style={{
						background: dirtyCount > 0 ? COPPER : controlsBg,
						color: dirtyCount > 0 ? 'white' : textMuted,
						opacity: saving ? 0.6 : 1,
						border: dirtyCount > 0 ? 'none' : `1px solid ${borderDefault}`,
					}}
				>
					{saving ? 'Saving…' : 'Save changes'}
				</button>
			</div>

			{importOpen && <ImportModal onCancel={() => setImportOpen(false)} onConfirm={handleImport} />}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function EnvField({
	draft,
	declared,
	onChange,
	onReveal,
}: {
	draft: FieldDraft;
	declared: AppEnvManifestEntry | undefined;
	onChange: (value: string) => void;
	onReveal: () => void;
}) {
	const isSecret = draft.type === 'secret';
	const inputType = isSecret && !draft.revealed ? 'password' : draft.type === 'url' ? 'url' : 'text';
	return (
		<div
			className="flex flex-col gap-2 rounded-xl border p-3.5"
			style={{
				borderColor: draft.error ? 'var(--khal-status-error)' : borderDefault,
				background: surfaceRaised,
			}}
		>
			<div className="flex items-center justify-between gap-2">
				<label
					htmlFor={`appenv-field-${draft.key}`}
					style={{
						fontSize: 12,
						color: textPrimary,
						fontWeight: 600,
						fontFamily: "'Geist Mono', monospace",
					}}
				>
					{draft.key}
					{declared?.required && <span style={{ color: 'var(--khal-status-error)', marginLeft: 3 }}>*</span>}
				</label>
				<span
					className="rounded-md px-1.5 py-0.5 font-medium"
					style={{
						fontSize: 10,
						color: textMuted,
						background: 'var(--khal-surface-default, #1a1a1a)',
						border: `1px solid ${borderDefault}`,
					}}
				>
					{draft.type}
				</span>
			</div>
			{declared?.description && (
				<span style={{ fontSize: 12, color: textMuted }}>{declared.description}</span>
			)}
			<div className="flex items-center gap-1.5">
				<input
					id={`appenv-field-${draft.key}`}
					data-testid={`appenv-field-${draft.key}`}
					type={inputType}
					value={draft.value}
					onChange={(e) => onChange(e.target.value)}
					className="w-full rounded-lg px-3 py-2"
					style={{
						fontSize: 13,
						fontFamily: "'Geist Mono', monospace",
						background: 'var(--khal-surface-default, #1a1a1a)',
						color: textPrimary,
						border: `1px solid ${borderDefault}`,
					}}
				/>
				{isSecret && (
					<button
						type="button"
						aria-label={draft.revealed ? 'Hide secret' : 'Reveal secret'}
						data-testid={`appenv-reveal-${draft.key}`}
						onClick={onReveal}
						className="flex h-8 w-8 items-center justify-center rounded-lg"
						style={{ color: textMuted, border: `1px solid ${borderDefault}` }}
					>
						{draft.revealed ? <EyeOff size={13} /> : <Eye size={13} />}
					</button>
				)}
			</div>
			{draft.error && (
				<span data-testid={`appenv-error-${draft.key}`} style={{ fontSize: 11, color: 'var(--khal-status-error)' }}>
					{draft.error}
				</span>
			)}
		</div>
	);
}

function ClaimBanner({
	keys,
	modifiedAt,
	onAdopt,
	onSkip,
}: {
	keys: string[];
	modifiedAt?: string;
	onAdopt: () => void;
	onSkip: () => void;
}) {
	return (
		<div
			data-testid="appenv-claim-banner"
			className="flex flex-col gap-2 rounded-xl border p-3.5"
			style={{
				background: 'rgba(234,179,8,0.08)',
				borderColor: 'rgba(234,179,8,0.4)',
				color: 'var(--khal-status-warning)',
				fontSize: 12,
			}}
		>
			<span>
				This app already has env values set outside this UI
				{modifiedAt && ` (last modified ${new Date(modifiedAt).toLocaleString()})`}. Adopt them so they appear here?
			</span>
			<details>
				<summary style={{ cursor: 'pointer', fontSize: 11 }}>{keys.length} keys</summary>
				<div style={{ fontSize: 11, color: textMuted, fontFamily: "'Geist Mono', monospace" }}>
					{keys.join(', ')}
				</div>
			</details>
			<div className="flex gap-1.5">
				<button
					type="button"
					data-testid="appenv-claim-adopt"
					onClick={onAdopt}
					className="rounded-lg px-3 py-1.5 text-[11px] font-semibold"
					style={{ background: 'var(--khal-status-warning)', color: 'var(--khal-bg)' }}
				>
					Adopt
				</button>
				<button
					type="button"
					data-testid="appenv-claim-skip"
					onClick={onSkip}
					className="rounded-lg px-3 py-1.5 text-[11px]"
					style={{ background: controlsBg, color: textMuted }}
				>
					Skip
				</button>
			</div>
		</div>
	);
}

function ImportModal({
	onCancel,
	onConfirm,
}: {
	onCancel: () => void;
	onConfirm: (blob: string) => void;
}) {
	const [text, setText] = useState('');
	const parsed = useMemo(() => parseEnvBlob(text), [text]);
	const proposed = parsed.entries.map((e) => ({ key: e.key, value: e.value }));
	const diff = diffEntries([], proposed);
	return (
		<div
			data-testid="appenv-import-modal"
			className="flex flex-col gap-3 rounded-xl border p-4"
			style={{ background: surfaceRaised, borderColor: borderDefault }}
		>
			<span style={{ fontSize: 13, color: textPrimary, fontWeight: 600 }}>
				Import environment variables
			</span>
			<textarea
				data-testid="appenv-import-textarea"
				value={text}
				onChange={(e) => setText(e.target.value)}
				rows={6}
				className="w-full rounded-lg p-3"
				style={{
					fontSize: 12,
					fontFamily: "'Geist Mono', monospace",
					background: 'var(--khal-surface-default, #1a1a1a)',
					color: textPrimary,
					border: `1px solid ${borderDefault}`,
				}}
				placeholder={'KEY=value\nOTHER="quoted value"'}
			/>
			<div style={{ fontSize: 11, color: textMuted }}>
				{parsed.entries.length} entries · {parsed.errors.length} errors · {parsed.warnings.length} warnings · {diff.added.length} new
			</div>
			{parsed.errors.length > 0 && (
				<ul style={{ fontSize: 11, color: 'var(--khal-status-error)' }}>
					{parsed.errors.slice(0, 3).map((e, i) => (
						<li key={i}>
							line {e.line}: {e.message}
						</li>
					))}
				</ul>
			)}
			{parsed.warnings.length > 0 && (
				<ul style={{ fontSize: 11, color: 'var(--khal-status-warning)' }}>
					{parsed.warnings.slice(0, 3).map((warning, i) => (
						<li key={i}>
							line {warning.line}: {warning.message}
						</li>
					))}
				</ul>
			)}
			<div className="flex justify-end gap-1.5">
				<button
					type="button"
					data-testid="appenv-import-cancel"
					onClick={onCancel}
					className="rounded-lg px-3 py-1.5 text-[11px]"
					style={{ background: controlsBg, color: textMuted }}
				>
					Cancel
				</button>
				<button
					type="button"
					data-testid="appenv-import-confirm"
					disabled={parsed.entries.length === 0 || parsed.errors.length > 0}
					onClick={() => onConfirm(text)}
					className="rounded-lg px-3 py-1.5 text-[11px] font-semibold"
					style={{
						background: COPPER,
						color: 'white',
						opacity: parsed.entries.length === 0 || parsed.errors.length > 0 ? 0.5 : 1,
					}}
				>
					Stage {parsed.entries.length} {parsed.entries.length === 1 ? 'change' : 'changes'}
				</button>
			</div>
		</div>
	);
}

function ToolbarButton({
	onClick,
	icon,
	children,
}: {
	onClick: () => void;
	icon: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px]"
			style={{
				background: controlsBg,
				color: textMuted,
				border: `1px solid ${borderDefault}`,
			}}
		>
			{icon}
			{children}
		</button>
	);
}
